import { TooltipArrow } from '@radix-ui/react-tooltip'
import { type UseQueryResult, useQueries, useQueryClient } from '@tanstack/react-query'
import { format, subMonths } from 'date-fns'
import { Fragment, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import ListAltRounded from '@/components/ui/icons/listaltrounded'
import MessageRounded from '@/components/ui/icons/message-rounded'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import { useCurrentInvestigation } from '@/hooks/useCurrentInvestigation'
import { useCurrentMedication } from '@/hooks/useCurrentMedication'
import usePatientOrderHistory from '@/lib/api/med-requests/order-history'
import http from '@/lib/http'
import { buildCategoryIndex, getOrderCategoryPath } from '@/lib/order-categories'
import {
  findCatalogueMedication,
  getChangedDirections,
  getConsultationUploads,
  getMedicationName
} from '@/lib/previous-orders'
import {
  cn,
  getMedRequestSubscriptionText,
  getOrderStatusBadgeClasses,
  getPreviousOrderDateToUse,
  isMedRequestSubscription,
  parseValidDate
} from '@/lib/utils'
import { useLinkedPatients } from '@/services/linkedPatients'
import { useGetMedications } from '@/services/medications'
import { useProductCategoryTree } from '@/services/productCategories'
import type { MedRequest } from '@/types/data'
import AttachmentPreview from './attachment-preview'
import { PreviousMedicationCard } from './previous-medication-card'

interface OrderStatusBadgeProps {
  stage: string
  className?: string
}

export function OrderStatusBadge({ stage = 'Further answers needed', className }: OrderStatusBadgeProps) {
  return (
    <span
      className={cn(
        'h-9 max-w-[30ch] truncate p-2 px-4 rounded-2xl border text-[13px] leading-[20px]',
        getOrderStatusBadgeClasses(stage),
        className
      )}
    >
      {stage}
    </span>
  )
}

export function SetDateIcon({ ...props }) {
  return (
    <svg width='24' height='24' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg' {...props}>
      <path
        d='M17 2C16.45 2 16 2.45 16 3V4H8V3C8 2.45 7.55 2 7 2C6.45 2 6 2.45 6 3V4H5C3.89 4 3.01 4.9 3.01 6L3 20C3 21.1 3.89 22 5 22H19C20.1 22 21 21.1 21 20V6C21 4.9 20.1 4 19 4H18V3C18 2.45 17.55 2 17 2ZM19 20H5V10H19V20ZM11 13C11 12.45 11.45 12 12 12C12.55 12 13 12.45 13 13C13 13.55 12.55 14 12 14C11.45 14 11 13.55 11 13ZM7 13C7 12.45 7.45 12 8 12C8.55 12 9 12.45 9 13C9 13.55 8.55 14 8 14C7.45 14 7 13.55 7 13ZM15 13C15 12.45 15.45 12 16 12C16.55 12 17 12.45 17 13C17 13.55 16.55 14 16 14C15.45 14 15 13.55 15 13ZM11 17C11 16.45 11.45 16 12 16C12.55 16 13 16.45 13 17C13 17.55 12.55 18 12 18C11.45 18 11 17.55 11 17ZM7 17C7 16.45 7.45 16 8 16C8.55 16 9 16.45 9 17C9 17.55 8.55 18 8 18C7.45 18 7 17.55 7 17ZM15 17C15 16.45 15.45 16 16 16C16.55 16 17 16.45 17 17C17 17.55 16.55 18 16 18C15.45 18 15 17.55 15 17Z'
        className={cn('fill-[#153CB7]', props.className)}
      />
    </svg>
  )
}

export function TooltipWrapper({ trigger, content }: { trigger: React.ReactNode; content: React.ReactNode }) {
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>{trigger}</TooltipTrigger>
        <TooltipContent className='rounded-[4px] p-2 w-[260px] bg-[rgba(97,97,97,0.90)]'>{content}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  )
}

type OrderProps = {
  order: MedRequest
  onOpenConversationModal?: (data: ConversationData) => void
}

export function Order({ order, onOpenConversationModal }: OrderProps) {
  const { data: medications } = useGetMedications()
  const selectedMedication = findCatalogueMedication(order, medications)
  const displayName = getMedicationName(order, selectedMedication)
  const isReorder = order.isReorder

  const isSubscription = isMedRequestSubscription(order.subscriptionFrequency)
  const renderOrderTypeTag = isReorder || isSubscription

  const subscriptionStr = getMedRequestSubscriptionText(order.subscriptionFrequency)

  const orderDate = getPreviousOrderDateToUse(order)

  const renderChangedDirections = getChangedDirections(order, selectedMedication) !== null

  const onHoldHistoryEntries = Array.isArray(order.onHoldHistory) ? order.onHoldHistory : null
  const hasOnHoldHistoryArray = !!onHoldHistoryEntries && onHoldHistoryEntries.length > 0

  const isTooltipAvailable = Boolean(
    renderChangedDirections ||
      hasOnHoldHistoryArray ||
      order.reasonForHold ||
      order.patientsResponseForHold ||
      order.reasonForRejection ||
      order.additionalNotes?.pharmacist?.length ||
      order.additionalNotes?.customerService?.length
  )

  const isConversationIconVisible = Boolean(order.latestFanConversationId || order.rejectionConversationId)

  return (
    <Link
      target='_blank'
      rel='noopener noreferrer'
      to={`/request/${order.id}`}
      className='flex min-h-[180px] flex-col justify-center px-8 py-4 gap-4 rounded-3xl border border-custom-neutral-200 bg-[#FAFAFB] cursor-pointer hover:border-[#153CB7] transition-colors'
    >
      <div className='flex w-full justify-between items-center'>
        <div>
          <p className='text-[#1D1D1D] text-sm font-bold leading-[170%]'>{displayName}</p>
          {order.orderedItem.category && (
            <p className='text-custom-neutral-800 text-sm leading-[170%] overflow-hidden truncate'>
              {order.orderedItem.category}
            </p>
          )}
        </div>
        <OrderStatusBadge stage={order.stage} />
      </div>
      <hr className='border-custom-neutral-200 border' />
      <div className='flex w-full justify-between items-center'>
        <div>
          <div className='flex items-center gap-2.5 mb-4'>
            <p className='text-custom-neutral-800 text-sm leading-[170%] w-max'>
              {orderDate?.getTime() ? format(orderDate, 'dd MMMM yyyy') : 'Invalid Date'}
            </p>
          </div>
          {renderOrderTypeTag && (
            <span className='h-9 max-w-[30ch] text-[#24272B] truncate p-2 px-4 rounded-2xl border text-[13px] leading-[20px] border-[#4E545E]'>
              {isReorder ? 'Re-order' : `Subscription ${subscriptionStr}`}
            </span>
          )}
        </div>
        <div className='flex items-center gap-2'>
          {isConversationIconVisible && (
            <button
              onClick={e => {
                e.preventDefault()
                e.stopPropagation()
                onOpenConversationModal?.({
                  fanConversationId: order.latestFanConversationId ?? '',
                  rejectionConversationId: order.rejectionConversationId ?? '',
                  medicationName: displayName,
                  medicationCategory: order.orderedItem.category ?? ''
                })
              }}
              type='button'
              className='flex w-12 h-12 p-2 justify-center items-center rounded-2xl border border-[#A0A3A6]'
            >
              <MessageRounded />
            </button>
          )}
          {isTooltipAvailable && (
            <TooltipWrapper
              trigger={
                <div className='flex w-12 h-12 p-2 justify-center items-center rounded-2xl border border-[#A0A3A6]'>
                  <ListAltRounded />
                </div>
              }
              content={
                <>
                  {renderChangedDirections && (
                    <>
                      <h3 className='text-sm font-bold text-white'>Changed directions</h3>
                      <p className='text-white text-xs font-medium leading-[14px]'>{order.medicationDirections}</p>
                    </>
                  )}
                  {hasOnHoldHistoryArray ? (
                    onHoldHistoryEntries!.map((entry, idx) => (
                      <Fragment key={`${entry.date}-${idx}`}>
                        {entry.reason && (
                          <>
                            <h3 className='text-sm font-bold text-white'>On hold reason</h3>
                            <p className='text-white text-xs font-medium leading-[14px]'>{entry.reason}</p>
                          </>
                        )}
                        {entry.patientResponse && (
                          <>
                            <h3 className='text-sm font-bold text-white'>Patient's response</h3>
                            <p className='text-white text-xs font-medium leading-[14px]'>{entry.patientResponse}</p>
                          </>
                        )}
                      </Fragment>
                    ))
                  ) : (
                    <>
                      {order.reasonForHold && (
                        <>
                          <h3 className='text-sm font-bold text-white'>On hold reason</h3>
                          <p className='text-white text-xs font-medium leading-[14px]'>{order.reasonForHold}</p>
                        </>
                      )}
                      {order.patientsResponseForHold && (
                        <>
                          <h3 className='text-sm font-bold text-white'>Patient's response</h3>
                          <p className='text-white text-xs font-medium leading-[14px]'>
                            {order.patientsResponseForHold}
                          </p>
                        </>
                      )}
                    </>
                  )}
                  {order.reasonForRejection && (
                    <>
                      <h3 className='text-sm font-bold text-white'>Rejection Reason</h3>
                      <p className='text-white text-xs font-medium leading-[14px]'>{order.reasonForRejection}</p>
                    </>
                  )}
                  {order.additionalNotes?.pharmacist && (
                    <>
                      <h3 className='text-sm font-bold text-white'>Pharmacy notes</h3>
                      <p className='text-white text-xs font-medium leading-[14px]'>
                        {order.additionalNotes.pharmacist}
                      </p>
                    </>
                  )}
                  {order.additionalNotes?.customerService && (
                    <>
                      <h3 className='text-sm font-bold text-white'>Customer Service notes</h3>
                      <p className='text-white text-xs font-medium leading-[14px]'>
                        {order.additionalNotes.customerService}
                      </p>
                    </>
                  )}
                  <TooltipArrow className='fill-[rgba(97,97,97,0.90)]' />
                </>
              }
            />
          )}
        </div>
      </div>
    </Link>
  )
}

export type ConversationData = {
  fanConversationId: string
  rejectionConversationId: string
  medicationName: string
  medicationCategory: string
}

type HistoryEntry = {
  order: MedRequest
  linkedLabel: string | null
  /** Whose history query the order came from — its cache key when refreshing presigned URLs. */
  historyPatientId: string
  orderedAt: Date | null
  medicationName: string
  changedDirections: string | null
  categoryPath: string[]
  imageCount: number
  workDriveLinks: string[]
}

const OTHER_CATEGORY = 'Other'

// Consultation image URLs are presigned for 300 s; a history fetch younger than this is reused as is.
const PRESIGNED_URL_REUSE_MS = 4 * 60 * 1000

const combineHistories = (results: UseQueryResult<MedRequest[]>[]) => results.map(result => result.data)

// Undated orders fall outside every date window: they sort last and only show once the full history is.
const isOrderedSince = (entry: HistoryEntry, date: Date) => !!entry.orderedAt && entry.orderedAt >= date

const rootCategoryOf = (entry: HistoryEntry) => entry.categoryPath[0] ?? OTHER_CATEGORY

function CategoryChip({
  label,
  count,
  isActive,
  onClick
}: {
  label: string
  count: number
  isActive: boolean
  onClick: () => void
}) {
  return (
    <button
      type='button'
      aria-pressed={isActive}
      onClick={onClick}
      className={cn(
        'rounded-full border px-3 py-1 text-[13px] leading-5 transition-colors cursor-pointer',
        isActive
          ? 'border-[#1D1D1D] bg-[#1D1D1D] text-white'
          : 'border-custom-neutral-700 bg-white text-custom-neutral-800 hover:border-custom-neutral-400'
      )}
    >
      {label} ({count})
    </button>
  )
}

export default function PreviousOrders({
  patientOrigin = 'medication-request',
  patientId
}: {
  patientId: string
  patientOrigin?: 'investigation' | 'medication-request'
}) {
  const queryClient = useQueryClient()
  const { data: previousOrders } = usePatientOrderHistory(patientId, !!patientId)
  const investigation = useCurrentInvestigation(patientOrigin === 'investigation')
  const medRequest = useCurrentMedication(
    !!(patientOrigin === 'medication-request' || !!investigation?.data?.medicationRequestId),
    investigation?.data?.medicationRequestId,
    false
  )
  const currentRequestId = medRequest?.data?.id

  // Linked patients: only relevant inside the medication-request origin — the investigation flow doesn't
  // surface a linked_patient_hash on its own, and resolving it would require an extra lookup we don't need.
  const linkedPatientHash = patientOrigin === 'medication-request' ? medRequest?.data?.patient?.linkedPatientHash : null
  const { data: linkedData } = useLinkedPatients(linkedPatientHash)

  const linkedOthers = useMemo(() => {
    if (!linkedData?.patients) return []
    return linkedData.patients.filter(lp => lp.hash !== patientId)
  }, [linkedData, patientId])

  // Parallel fetch — one order history query per linked patient. Same cache key as the current-patient
  // fetch above so anything already warmed in the query cache is reused.
  const linkedHistories = useQueries({
    queries: linkedOthers.map(lp => ({
      queryKey: ['patientOrderHistory', lp.hash],
      queryFn: async (): Promise<Array<MedRequest>> => http.get(`/prescriber/med-requests/history/${lp.hash}`),
      enabled: !!lp.hash
    })),
    combine: combineHistories
  })

  const { data: medications } = useGetMedications()
  const { data: categoryTree } = useProductCategoryTree()
  const categoryIndex = useMemo(() => (categoryTree ? buildCategoryIndex(categoryTree) : null), [categoryTree])

  const [twelveMonthsAgo] = useState(() => subMonths(new Date(), 12))
  // null once the full history is shown, undated orders included.
  const [windowStart, setWindowStart] = useState<Date | null>(twelveMonthsAgo)
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null)
  const [galleryImages, setGalleryImages] = useState<string[] | null>(null)
  const [refreshingImagesForId, setRefreshingImagesForId] = useState<string | null>(null)

  const entries = useMemo<HistoryEntry[]>(() => {
    const own = (previousOrders ?? []).map(order => ({ order, linkedLabel: null, historyPatientId: patientId }))
    const linked = linkedOthers.flatMap((linkedPatient, idx) =>
      (linkedHistories[idx] ?? []).map(order => ({
        order,
        linkedLabel: `Patient ${idx + 1}`,
        historyPatientId: linkedPatient.hash
      }))
    )

    return [...own, ...linked]
      .filter(({ order }) => order.id !== currentRequestId)
      .map(entry => {
        const medication = findCatalogueMedication(entry.order, medications)
        const { imageUrls, workDriveLinks } = getConsultationUploads(entry.order)
        return {
          ...entry,
          orderedAt: parseValidDate(entry.order.creationDate),
          medicationName: getMedicationName(entry.order, medication),
          changedDirections: getChangedDirections(entry.order, medication),
          categoryPath: getOrderCategoryPath(
            { snapshot: entry.order.orderedItem.category, categoryHashes: medication?.categoryHashes },
            categoryIndex
          ),
          imageCount: imageUrls.length,
          workDriveLinks
        }
      })
      .sort((a, b) => (b.orderedAt?.getTime() ?? 0) - (a.orderedAt?.getTime() ?? 0))
  }, [previousOrders, linkedOthers, linkedHistories, currentRequestId, patientId, medications, categoryIndex])

  if (!entries.length) return null

  const recentCount = entries.filter(entry => isOrderedSince(entry, twelveMonthsAgo)).length
  const isInWindow = (entry: HistoryEntry) => !windowStart || isOrderedSince(entry, windowStart)
  const entriesInWindow = entries.filter(isInWindow)

  const categoryCounts = new Map<string, number>()
  for (const entry of entriesInWindow) {
    const category = rootCategoryOf(entry)
    categoryCounts.set(category, (categoryCounts.get(category) ?? 0) + 1)
  }
  const categories = [...categoryCounts].sort(
    ([aName, aCount], [bName, bCount]) => bCount - aCount || aName.localeCompare(bName)
  )
  const activeCategory = selectedCategory && categoryCounts.has(selectedCategory) ? selectedCategory : null
  const isInActiveCategory = (entry: HistoryEntry) => !activeCategory || rootCategoryOf(entry) === activeCategory
  const entriesInCategory = entriesInWindow.filter(isInActiveCategory)
  // Scoped to the active chip, so the count matches what a click reveals.
  const olderEntries = entries.filter(entry => !isInWindow(entry) && isInActiveCategory(entry))

  // Steps back a year at a time, skipping empty years so every click reveals something.
  const loadMore = () => {
    if (!windowStart || !olderEntries.length) return
    const nextOrderedAt = olderEntries[0].orderedAt
    if (!nextOrderedAt) {
      // Only undated orders are left.
      setWindowStart(null)
      return
    }
    let next = subMonths(windowStart, 12)
    while (nextOrderedAt < next) next = subMonths(next, 12)
    setWindowStart(next)
  }

  const openImages = async (entry: HistoryEntry) => {
    // One refresh at a time, or the first to finish would clear the other's spinner and both galleries open.
    if (refreshingImagesForId) return

    const queryKey = ['patientOrderHistory', entry.historyPatientId]
    const fetchedAt = queryClient.getQueryState(queryKey)?.dataUpdatedAt ?? 0

    if (Date.now() - fetchedAt > PRESIGNED_URL_REUSE_MS) {
      setRefreshingImagesForId(entry.order.id)
      await queryClient.refetchQueries({ queryKey, exact: true })
      setRefreshingImagesForId(null)
    }

    const freshOrder = queryClient.getQueryData<MedRequest[]>(queryKey)?.find(order => order.id === entry.order.id)
    setGalleryImages(getConsultationUploads(freshOrder ?? entry.order).imageUrls)
  }

  return (
    <div id='previous-orders' className='rounded-2xl border border-custom-neutral-700 bg-white p-8'>
      <div className='mb-6 flex flex-wrap items-start justify-between gap-4'>
        <div>
          <p className='text-custom-neutral-600 text-xl font-semibold leading-[133.4%]'>Previous medications</p>
          <p data-testid='previous-medications-count' className='mt-1 text-sm leading-5 text-custom-neutral-500'>
            {recentCount} {recentCount === 1 ? 'order' : 'orders'} in the last 12 months
            {entries.length > recentCount && ` · ${entries.length} in total`}
          </p>
        </div>
        {entriesInWindow.length > 0 && (
          <div className='flex flex-wrap justify-end gap-2'>
            <CategoryChip
              label='All'
              count={entriesInWindow.length}
              isActive={!activeCategory}
              onClick={() => setSelectedCategory(null)}
            />
            {categories.map(([name, count]) => (
              <CategoryChip
                key={name}
                label={name}
                count={count}
                isActive={activeCategory === name}
                onClick={() => setSelectedCategory(name)}
              />
            ))}
          </div>
        )}
      </div>

      <div className='flex max-h-[720px] flex-col gap-4 overflow-y-auto pr-1'>
        {entriesInCategory.length ? (
          entriesInCategory.map(entry => (
            <PreviousMedicationCard
              key={entry.order.id}
              order={entry.order}
              orderedAt={entry.orderedAt}
              medicationName={entry.medicationName}
              changedDirections={entry.changedDirections}
              categoryPath={entry.categoryPath}
              imageCount={entry.imageCount}
              workDriveLinks={entry.workDriveLinks}
              isOpeningImages={refreshingImagesForId === entry.order.id}
              linkedLabel={entry.linkedLabel}
              onOpenImages={() => openImages(entry)}
            />
          ))
        ) : (
          <p className='rounded-2xl border border-dashed border-custom-neutral-700 px-6 py-8 text-center text-sm leading-5 text-custom-neutral-600'>
            No orders in the last 12 months.
          </p>
        )}
        {olderEntries.length > 0 && (
          <Button type='button' variant='outline' className='shrink-0 self-center rounded-2xl' onClick={loadMore}>
            Load more ({olderEntries.length} older {olderEntries.length === 1 ? 'order' : 'orders'})
          </Button>
        )}
      </div>

      {galleryImages && <AttachmentPreview images={galleryImages} onClose={() => setGalleryImages(null)} />}
    </div>
  )
}
